import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class SectionDetailsService {

  constructor(private http: HttpClient) { }

  // 🚀 CRITICAL: Following SectionDetailsFactory.fetchSectionDetails pattern exactly
  fetchSectionDetails(
    baseUrl: string,
    sectionNumber: string,
    sso_id: string,
    occur_index: string,
    tax_year: string,
    scenario: string,
    cc_key: string
  ): Observable<any> {
    
    // 🚀 CRITICAL: URL construction with action codes like AngularJS service
    const url = `${baseUrl}/loadJsonObject`;
    
    // Build params exactly like AngularJS
    const params = new HttpParams()
      .set('action_code', '5apopf')
      .set('sso_id', sso_id)
      .set('tax_year', tax_year)
      .set('scenario', scenario)
      .set('jcd_key', '250')
      .set('combination_key', cc_key)
      .set('form_no', '4797')
      .set('prop_section', sectionNumber)
      .set('occurrence', occur_index);

    return this.http.get<any>(url, { params }).pipe(
      map((data: any) => {
        if (data.callSuccess === "1") {
          return data;
        } else {
          throw new Error(data.errorMessage || 'Error fetching section details');
        }
      })
    );
  }

  // 🚀 CRITICAL: Following SectionDetailsFactory.saveSection pattern exactly
  saveSection(
    baseUrl: string,
    final_data: any[],
    clob_settings: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    
    // 🚀 CRITICAL: URL construction with action codes like AngularJS service
    const url = `${baseUrl}/saveJsonObject?action_code=7w7k2j`;

    // 🚀 CRITICAL: Build AngularJS-compatible payload structure
    let params: any = _.merge({}, globalService || {}, filterParams);
    
    // Build JSON objects exactly like AngularJS service
    const json_settings = JSON.stringify(clob_settings || []);
    const json_data = JSON.stringify(final_data || []);
    
    // Extend with json_data and json_settings (AngularJS uses json_data and json_settings, not clob_data/clob_settings)
    // 🚀 CRITICAL: Extend params (destination) with new values (source) so stringified values take precedence
    params = _.extend(params, { json_data: json_data });
    params = _.extend(params, { json_settings: json_settings });
    
    // 🚀 CRITICAL: Ensure all required fields are present with proper fallbacks
    params.tax_year = filterParams?.tax_year || globalService?.tax_year || rowData?.TAX_YEAR || '';
    params.screen_key = filterParams?.screen_key || globalService?.screen_key || globalService?.screen_key || '';
    params.scenario = filterParams?.scenario || globalService?.scenario || rowData?.SCENARIO || '';
    params.jcd_key = filterParams?.jcd_key || globalService?.jcd_key || rowData?.JCD_KEY || '';
    params.jcd_ta_key = filterParams?.jcd_ta_key || globalService?.jcd_ta_key || '';
    params.activity_key = filterParams?.activity_key || globalService?.activity_key || '';
    params.screen_type = filterParams?.screen_type || globalService?.screen_type || '';
    params.is_locked = filterParams?.is_locked || globalService?.is_locked || 'N';
    params.issue_id = filterParams?.issue_id || globalService?.issue_id || '';
    params.issue_short_desc = filterParams?.issue_short_desc || globalService?.issue_short_desc || '';
    params.issue_id_for_tx_save = filterParams?.issue_id_for_tx_save || globalService?.issue_id_for_tx_save || '';
    params.scenarioCode = filterParams?.scenarioCode || globalService?.scenarioCode || 'RAF';
    params.is_issue_key_enabled = filterParams?.is_issue_key_enabled || globalService?.is_issue_key_enabled || 'N';
    params.filing_key = filterParams?.filing_key || globalService?.filing_key || rowData?.CONSOL_GROUP_KEY || '';
    params.filter_by = filterParams?.filter_by || globalService?.filter_by || '';
    params.parent_me_string = filterParams?.parent_me_string || globalService?.parent_me_string || '';

    // 🚀 CRITICAL: Build checkLockedbj from rowData (NOT hardcoded)
    const checkLockedbj: any[] = [];
    if (rowData && Object.keys(rowData).length > 0) {
      // Handle single rowData object
      checkLockedbj.push({
        "leid": rowData.LEID || rowData.SOURCE_LEID || '',
        "me_code": rowData.ME_CODE || 'CJ7000',
        "cdr_number": rowData.CDR_NO || rowData.TAX_CODE || rowData.LEID || '',
        "consol_group_key": String(rowData.CONSOL_GROUP_KEY || filterParams?.consol_group_key || '1'),
        "reporting_period": String(rowData.REPORTING_PERIOD || filterParams?.reporting_period || '1'),
        "combination_key": String(rowData.COMBINATION_KEY || filterParams?.combination_key || ''),
        "scenario_key": String(filterParams?.scenario || globalService?.scenario || rowData?.SCENARIO || '74')
      });
    } else if (filterParams && Object.keys(filterParams).length > 0) {
      // Fallback to filterParams if rowData is not available
      checkLockedbj.push({
        "leid": filterParams?.leid || '',
        "me_code": filterParams?.me_code || 'CJ7000',
        "cdr_number": filterParams?.cdr_number || '',
        "consol_group_key": String(filterParams?.consol_group_key || '1'),
        "reporting_period": String(filterParams?.reporting_period || '1'),
        "combination_key": String(filterParams?.combination_key || ''),
        "scenario_key": String(filterParams?.scenario || '74')
      });
    }
    params.checkLockedbj = JSON.stringify(checkLockedbj);
    
    // Set process_name exactly like AngularJS
    params.process_name = "form-4797-prop-section-save";

    // 🚀 CRITICAL: Use application/x-www-form-urlencoded (NOT json)
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      urlEncoded.set(key, params[key]);
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };

    return this.http.post<any>(url, urlEncoded.toString(), httpOptions);
  }
}

